import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Glasses, Plus, Receipt, ShoppingCart, Trash2, X } from 'lucide-react';
import { ReasonCode } from '@optiflow/frame-recommendation';
import {
  ApiErrorCode,
  PERMISSIONS,
  ProductType,
  type BasketValidation,
  type ProductSummary,
  type ProductVariantSummary,
  type SalesOrderDetail,
  type TaxProfileSummary,
} from '@optiflow/types';
import { computeLineTax } from '@optiflow/shared';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { useAuth } from '@/hooks/useAuth';
import { useConnectivity } from '@/hooks/useConnectivity';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorCode, apiErrorDetails, apiErrorMessage } from '@/services/apiClient';
import { useProductQuery } from '@/modules/catalog/api/catalog.api';
import { ProductSearch } from '@/modules/catalog/components/ProductSearch';
import { ProductThumb } from '@/modules/catalog/components/ProductThumb';
import { CUSTOMER_INTENT_OPTICAL_ORDER } from '@/modules/customers/intents';
import { OPTICAL_BUILDER_PERMISSIONS } from '@/modules/optical-orders/permissions';
import { cn } from '@/utils/cn';
import {
  useCreateCounterSaleMutation,
  useTaxProfilesQuery,
  useValidateBasketMutation,
  type Tender,
} from '../api/sales.api';
import { TenderPanel } from '../components/TenderPanel';
import { ShortcutLegend } from '../components/ShortcutLegend';
import {
  clearDraft,
  draftKey,
  loadDraft,
  newLineId,
  saveDraft,
  setAsideUnresolvedDraft,
  takeBackUnresolvedDraft,
} from '../basketDraft';
import { matchResultsByLineKey, refusedRecommendationLineIds } from '../basketReconciliation';
import { readHandoff, type FrameRecommendationHandoff } from '../recommendationHandoff';
import {
  readSelectionClaims,
  recommendationFromToken,
  sameRecommendation,
  type LineRecommendation,
} from '../recommendationProvenance';
import { useCounterHotkeys } from '../useCounterHotkeys';

interface BasketLine {
  /** The line's own identity. The same frame can be in the basket twice — sold by hand and from a recommendation — so neither the frame nor the position identifies a line. */
  lineId: string;
  variantId: string;
  sku: string;
  name: string;
  unitPriceMinor: number;
  quantity: number;
  discountMinor: number;
  /** True when the goods go to the lab rather than home with the customer. */
  collectLater: boolean;
  /** Set when the frame came from a recommendation; null for a line rung up by hand. */
  recommendation: LineRecommendation | null;
}

/** Reason codes the counter can name, in the recommendation screen's own words; a code from a newer rules version is left out rather than shown raw. */
const KNOWN_REASON_CODES = new Set<string>(Object.values(ReasonCode));

const CUSTOMER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The counter.
 *
 * Totals are recomputed here on every keystroke so the person at the till sees the same number the
 * customer is about to be asked for — but they are advisory. The server prices the basket from the
 * catalogue and its own tax profiles, and its figures are the ones that get recorded.
 *
 * A frame can also arrive from the recommendations screen. It is taken once, after this visit's saved basket has been dealt with, priced by the server like any other line, and kept with its recommendation's signed token; checkout verifies that token, and a recommendation that cannot be confirmed is removed only when the cashier chooses to.
 */
export function CounterSalePage() {
  const { t } = useTranslation();
  const { activeBranch } = useBranch();
  const { format, currencyCode } = useCurrency();
  const { can, canAll } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const [lines, setLines] = useState<BasketLine[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [taxProfileId, setTaxProfileId] = useState('');
  const [tenders, setTenders] = useState<Tender[]>([]);
  // An amount typed into the tender form but not added is not a payment, so checkout waits until it is added or cleared.
  const [tenderPending, setTenderPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState<SalesOrderDetail | null>(null);
  const [pendingProduct, setPendingProduct] = useState<ProductSummary | null>(null);
  /** Which line the basket keys act on. Null until the cashier moves into the list. */
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [validation, setValidation] = useState<BasketValidation | null>(null);
  const [needsAcknowledgement, setNeedsAcknowledgement] = useState(false);
  /** Bumped when a failed basket is set aside, so a basket rung up while its check was in flight is saved into the freed slot at once. */
  const [freedDraftSlot, setFreedDraftSlot] = useState(0);
  /** True once this visit's saved basket, if there was one, has been restored or set aside; a recommended frame waits for it. */
  const [restoreSettled, setRestoreSettled] = useState(false);
  /** A recommended frame for a different customer than this basket's, waiting for the cashier to choose. */
  const [customerConflict, setCustomerConflict] = useState<FrameRecommendationHandoff | null>(null);
  /** A customer typed while recommended lines are in the basket, waiting for the cashier to confirm. */
  const [pendingCustomer, setPendingCustomer] = useState<string | null>(null);
  const [addingRecommendation, setAddingRecommendation] = useState(false);
  /** Lines whose recommendation checkout could not confirm. */
  const [refusedLineIds, setRefusedLineIds] = useState<ReadonlySet<string>>(new Set());
  /** A prescription lens was chosen: it is made for a pair in an optical order, so it is not added here. */
  const [lensRefused, setLensRefused] = useState(false);
  /** Asking whether to leave a basket with lines for a separate optical order. */
  const [confirmingOpticalOrder, setConfirmingOpticalOrder] = useState(false);

  const scanArea = useRef<HTMLDivElement>(null);
  const customerField = useRef<HTMLInputElement>(null);
  const tenderArea = useRef<HTMLDivElement>(null);
  /** The draft slot whose saved basket this visit has already decided about — one to check, or none; a ref, so React's development double run of effects cannot ask twice, and a basket the counter saves into that slot itself is never taken for one to restore. */
  const restoreDecidedFor = useRef<string | null>(null);
  /** The storage key of a saved basket that has not passed its check and is still held in the current slot; automatic saving and checkout cleanup leave that draft exactly as it was stored. */
  const unconfirmedDraftKey = useRef<string | null>(null);
  /** Hand-offs this visit has already taken; a ref, so a double run of effects cannot take one twice. */
  const takenHandoffs = useRef(new Set<string>());

  const taxProfiles = useTaxProfilesQuery();
  const createSale = useCreateCounterSaleMutation();
  const validateBasket = useValidateBasketMutation();
  // Its own mutation, so pricing a recommended frame never changes the restore check's identity and sets that effect running again.
  const checkRecommendedFrame = useValidateBasketMutation();
  const { isOnline } = useConnectivity();
  const { user } = useAuth();
  const canSellRecommendations = can(PERMISSIONS.FRAME_RECOMMEND);
  const canStartOpticalOrder = canAll(OPTICAL_BUILDER_PERMISSIONS);

  const draftScope = useMemo(
    () => ({
      tenantId: user?.tenantId ?? null,
      branchId: activeBranch?.id ?? null,
      userId: user?.id ?? null,
    }),
    [user?.tenantId, user?.id, activeBranch?.id],
  );

  const activeProfile: TaxProfileSummary | undefined = useMemo(() => {
    const all = taxProfiles.data ?? [];
    return all.find((p) => p.id === taxProfileId) ?? all.find((p) => p.isDefault) ?? all[0];
  }, [taxProfiles.data, taxProfileId]);

  /** Mirrors the server's per-line arithmetic, using the same shared helper. */
  const totals = useMemo(() => {
    if (!activeProfile) return { subtotal: 0, tax: 0, discount: 0, total: 0 };

    return lines.reduce(
      (acc, line) => {
        const tax = computeLineTax({
          grossBeforeDiscountMinor: line.unitPriceMinor * line.quantity,
          discountMinor: line.discountMinor,
          rateBasisPoints: activeProfile.rateBasisPoints,
          treatment: activeProfile.treatment,
          pricesIncludeTax: activeProfile.pricesIncludeTax,
        });

        return {
          subtotal: acc.subtotal + tax.netMinor,
          tax: acc.tax + tax.taxMinor,
          discount: acc.discount + line.discountMinor,
          total: acc.total + tax.totalMinor,
        };
      },
      { subtotal: 0, tax: 0, discount: 0, total: 0 },
    );
  }, [lines, activeProfile]);

  const tendered = tenders.reduce((sum, tender) => sum + tender.amountMinor, 0);
  const outstanding = totals.total - tendered;
  const recommendedLineCount = lines.filter((line) => line.recommendation !== null).length;

  const addVariant = (variant: ProductVariantSummary, product: ProductSummary) => {
    setError(null);
    setPendingProduct(null);
    // A prescription lens is made for one pair, with its prescription and frame: that is an optical order, never a counter line.
    if (product.type === ProductType.PRESCRIPTION_LENS) {
      setLensRefused(true);
      return;
    }
    setLensRefused(false);

    setLines((current) => {
      // A frame rung up by hand joins only a line that was also rung up by hand, never a recommended one.
      const existing = current.find(
        (line) => line.variantId === variant.id && line.recommendation === null,
      );
      if (existing) {
        return current.map((line) =>
          line.lineId === existing.lineId ? { ...line, quantity: line.quantity + 1 } : line,
        );
      }

      return [
        ...current,
        {
          lineId: newLineId(),
          variantId: variant.id,
          sku: variant.sku,
          name: variant.variantName ? `${product.name} — ${variant.variantName}` : product.name,
          unitPriceMinor: variant.sellingPriceMinor,
          quantity: 1,
          discountMinor: 0,
          collectLater: false,
          recommendation: null,
        },
      ];
    });
  };

  /**
   * A browsed product may have several variants — a frame in three colours, a lens in five powers.
   * Guessing which one the customer meant is worse than asking.
   */
  const handleProduct = (product: ProductSummary) => {
    setError(null);
    if (product.type === ProductType.PRESCRIPTION_LENS) {
      setPendingProduct(null);
      setLensRefused(true);
      return;
    }
    setLensRefused(false);
    setPendingProduct(product);
  };

  /**
   * Opens the optical order builder, which alone puts pairs together. The customer typed here goes with it; without one, the customer list is where one is chosen.
   *
   * Nothing in this basket moves or changes: it stays saved in its own slot for when the cashier comes back, and the optical order is a separate order.
   */
  const openOpticalOrder = () => {
    setConfirmingOpticalOrder(false);
    const chosen = customerId.trim();
    if (CUSTOMER_ID.test(chosen)) {
      void navigate(`/customers/${chosen}/optical-orders/new`);
      return;
    }
    void navigate('/customers', { state: { intent: CUSTOMER_INTENT_OPTICAL_ORDER } });
  };

  const startOpticalOrder = () => {
    if (lines.length > 0) {
      setConfirmingOpticalOrder(true);
      return;
    }
    openOpticalOrder();
  };

  /**
   * Brings back a basket the browser lost.
   *
   * Only the identifiers were stored, so the names and prices are re-read from the catalogue on the
   * way in — which also means a restored basket can never show a price that has since moved.
   */
  useEffect(() => {
    const slot = draftKey(draftScope);
    if (slot === null || restoreDecidedFor.current === slot) return;
    // Decided now for this slot, draft or not: this effect runs again after every render, and the counter's own autosave must never come back as a basket to restore. A branch switched to is another slot, whose saved basket is checked rather than written over.
    restoreDecidedFor.current = slot;
    // A basket set aside by an earlier failed check comes back only when no newer basket holds the current slot, so restoring it can never write over one.
    const draft =
      loadDraft(draftScope) ?? (takeBackUnresolvedDraft(draftScope) ? loadDraft(draftScope) : null);
    if (!draft) {
      setRestoreSettled(true);
      return;
    }

    unconfirmedDraftKey.current = slot;
    setRestoreSettled(false);
    setNeedsAcknowledgement(true);

    // Offline, the check failed, or its answer cannot be matched line for line: nothing is restored, this slot is not asked about again on this visit, and the basket is set aside byte for byte for a later visit, so a new basket saves normally and neither Clear nor a completed sale can remove it; if an earlier failed basket already holds that place, this one stays held in the current slot instead.
    const failed = () => {
      if (setAsideUnresolvedDraft(draftScope)) {
        unconfirmedDraftKey.current = null;
        setFreedDraftSlot((count) => count + 1);
      }
      setNeedsAcknowledgement(false);
      setRestoreSettled(true);
    };

    void validateBasket
      .mutateAsync({
        lines: draft.lines.map((line) => ({
          variantId: line.variantId,
          quantity: line.quantity,
          lineKey: line.lineId,
        })),
        ...(draft.customerId ? { customerId: draft.customerId } : {}),
        ...(draft.taxProfileId ? { taxProfileId: draft.taxProfileId } : {}),
      })
      .then((result) => {
        const checked = matchResultsByLineKey(draft.lines, result.lines);
        if (!checked) {
          failed();
          return;
        }

        // Checked, so the draft may follow the basket again: the save below rewrites it from the restored lines, or clears it when nothing could be restored.
        unconfirmedDraftKey.current = null;
        setValidation(result);
        setLines(
          draft.lines.flatMap((line): BasketLine[] => {
            const answer = checked.get(line.lineId);
            if (!answer || answer.issues.includes('UNAVAILABLE')) return [];
            return [
              {
                lineId: line.lineId,
                variantId: line.variantId,
                sku: answer.sku,
                name: answer.productName,
                unitPriceMinor: answer.unitPriceMinor,
                quantity: answer.quantity,
                discountMinor: 0,
                collectLater: line.collectLater,
                recommendation: line.recommendation,
              },
            ];
          }),
        );
        if (draft.customerId) setCustomerId(draft.customerId);
        if (draft.taxProfileId) setTaxProfileId(draft.taxProfileId);
        setRestoreSettled(true);
      }, failed);
  }, [draftScope, validateBasket]);

  // Written on every change, so a reload mid-sale costs nothing; a saved basket still held for its check is left untouched until it is set aside or gone.
  useEffect(() => {
    const key = draftKey(draftScope);
    if (key !== null && key === unconfirmedDraftKey.current) {
      if (loadDraft(draftScope)) return;
      unconfirmedDraftKey.current = null;
    }
    saveDraft(draftScope, {
      customerId: customerId || null,
      taxProfileId: taxProfileId || null,
      lines: lines.map((line) => ({
        lineId: line.lineId,
        variantId: line.variantId,
        quantity: line.quantity,
        discountMinor: line.discountMinor,
        collectLater: line.collectLater,
        recommendation: line.recommendation,
      })),
    });
  }, [draftScope, lines, customerId, taxProfileId, freedDraftSlot]);

  /** Prices a recommended frame through the server and puts it in the basket for its customer. */
  const addRecommendedFrame = async (
    handoff: FrameRecommendationHandoff,
    recommendation: LineRecommendation,
  ) => {
    const lineId = newLineId();
    setAddingRecommendation(true);
    try {
      const result = await checkRecommendedFrame.mutateAsync({
        lines: [{ variantId: handoff.variantId, quantity: 1, lineKey: lineId }],
        customerId: recommendation.customerId,
      });
      const answer = matchResultsByLineKey(
        [{ lineId, variantId: handoff.variantId }],
        result.lines,
      )?.get(lineId);
      if (!answer) {
        setError(t('sales.counter.handoff.failed'));
        return;
      }
      if (answer.issues.includes('UNAVAILABLE') || answer.issues.includes('INACTIVE')) {
        setError(t('sales.counter.handoff.unavailable'));
        return;
      }

      setError(null);
      setLines((current) => {
        const same = current.find(
          (line) =>
            line.variantId === handoff.variantId &&
            sameRecommendation(line.recommendation, recommendation),
        );
        if (same) {
          return current.map((line) =>
            line.lineId === same.lineId ? { ...line, quantity: line.quantity + 1 } : line,
          );
        }
        return [
          ...current,
          {
            lineId,
            variantId: handoff.variantId,
            sku: answer.sku,
            name: handoff.variantName
              ? `${handoff.productName} — ${handoff.variantName}`
              : handoff.productName,
            unitPriceMinor: answer.unitPriceMinor,
            quantity: 1,
            discountMinor: 0,
            collectLater: false,
            recommendation,
          },
        ];
      });
      setCustomerId(recommendation.customerId);
    } catch {
      setError(t('sales.counter.handoff.failed'));
    } finally {
      setAddingRecommendation(false);
    }
  };

  /** Decides how a recommended frame joins this basket: straight in for an empty or anonymous basket or the same customer, or only after the cashier chooses when the basket is someone else's. */
  const receiveHandoff = (handoff: FrameRecommendationHandoff) => {
    const recommendation = recommendationFromToken(handoff.selectionToken);
    const claims = readSelectionClaims(handoff.selectionToken);
    if (
      !recommendation ||
      !claims ||
      claims.customerId.toLowerCase() !== handoff.customerId.toLowerCase() ||
      claims.variantId.toLowerCase() !== handoff.variantId.toLowerCase()
    ) {
      setError(t('sales.counter.handoff.invalid'));
      return;
    }

    if (customerId !== '' && customerId.toLowerCase() !== recommendation.customerId.toLowerCase()) {
      setCustomerConflict(handoff);
      return;
    }
    void addRecommendedFrame(handoff, recommendation);
  };

  const handoff = useMemo(() => readHandoff(location.state), [location.state]);
  const receiveHandoffRef = useRef(receiveHandoff);
  useEffect(() => {
    receiveHandoffRef.current = receiveHandoff;
  });

  useEffect(() => {
    if (!handoff || !restoreSettled || takenHandoffs.current.has(handoff.handoffId)) return;
    takenHandoffs.current.add(handoff.handoffId);
    // Taken now, so Back, Forward, a re-render or a refresh finds no hand-off left in this entry to add again.
    void navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: null },
    );
    receiveHandoffRef.current(handoff);
  }, [handoff, restoreSettled, navigate, location.pathname, location.search, location.hash]);

  const updateLine = (lineId: string, patch: Partial<BasketLine>) => {
    setLines((current) =>
      current.map((line) => (line.lineId === lineId ? { ...line, ...patch } : line)),
    );
  };

  const removeLine = (lineId: string) => {
    setLines((current) => current.filter((line) => line.lineId !== lineId));
  };

  const removeRecommendation = (lineId: string) => {
    updateLine(lineId, { recommendation: null });
    setRefusedLineIds((current) => new Set([...current].filter((id) => id !== lineId)));
  };

  /** The customer field: a change that would leave recommended lines with someone else's recommendation asks first. */
  const changeCustomer = (next: string) => {
    if (next === customerId) return;
    if (recommendedLineCount > 0) {
      setPendingCustomer(next);
      return;
    }
    setCustomerId(next);
  };

  const confirmCustomerChange = () => {
    if (pendingCustomer === null) return;
    // The recommendations were for the previous customer; the frames stay as ordinary lines and no recommendation moves to the new customer.
    setLines((current) =>
      current.map((line) => (line.recommendation ? { ...line, recommendation: null } : line)),
    );
    setRefusedLineIds(new Set());
    setCustomerId(pendingCustomer);
    setPendingCustomer(null);
  };

  const clearBasket = () => {
    setLines([]);
    setTenders([]);
    setCustomerId('');
    setError(null);
    setRefusedLineIds(new Set());
  };

  const handleCheckout = () => {
    setError(null);

    if (lines.length === 0) {
      setError(t('sales.validation.emptyBasket'));
      return;
    }
    if (tendered > totals.total) {
      setError(t('sales.validation.overTendered'));
      return;
    }
    // F8 reaches here without the button, so the unadded amount is refused here as well.
    if (tenderPending) {
      setError(t('sales.tender.notAdded'));
      return;
    }
    if (lines.some((line) => line.collectLater) && !customerId) {
      setError(t('sales.validation.customerRequiredForCollection'));
      return;
    }

    // The server names a refused line by its place in this request, so the ids are kept in the same order.
    const submittedLineIds = lines.map((line) => line.lineId);

    createSale.mutate(
      {
        ...(customerId ? { customerId } : {}),
        lines: lines.map((line) => ({
          variantId: line.variantId,
          quantity: line.quantity,
          ...(line.recommendation
            ? { recommendationToken: line.recommendation.selectionToken }
            : {}),
          ...(line.collectLater ? { fulfilmentMode: 'DEFERRED_COLLECTION' as const } : {}),
          ...(line.discountMinor > 0 ? { discountMinor: line.discountMinor } : {}),
          ...(taxProfileId ? { taxProfileId } : {}),
        })),
        ...(tenders.length > 0 ? { tenders } : {}),
      },
      {
        onSuccess: (order) => {
          setCompleted(order);
          clearBasket();
          // A basket still held after its check failed is not the one just sold, so it stays.
          if (draftKey(draftScope) !== unconfirmedDraftKey.current) clearDraft(draftScope);
        },
        onError: (caught) => {
          if (apiErrorCode(caught) === ApiErrorCode.RECOMMENDATION_PROVENANCE_INVALID) {
            const named = refusedRecommendationLineIds(apiErrorDetails(caught), submittedLineIds);
            // When the refusal names no line it can be matched to, every recommended line is offered for removal; none is removed without the cashier.
            setRefusedLineIds(
              new Set(
                named.length > 0
                  ? named
                  : lines.filter((line) => line.recommendation).map((line) => line.lineId),
              ),
            );
            setError(t('sales.counter.recommendation.checkoutRefused'));
            return;
          }
          setError(apiErrorMessage(caught) ?? t('sales.errors.checkout'));
        },
      },
    );
  };

  /**
   * A short tone when a scan finds nothing.
   *
   * Web Audio rather than an asset, so nothing has to ship. At a counter the cashier is looking at
   * the customer and the goods, not the screen, and a silent failure means the item is bagged unpaid.
   */
  const soundScanFailure = useCallback(() => {
    try {
      const audio = new AudioContext();
      const oscillator = audio.createOscillator();
      const gain = audio.createGain();

      oscillator.frequency.value = 220;
      gain.gain.value = 0.06;
      oscillator.connect(gain).connect(audio.destination);
      oscillator.start();
      oscillator.stop(audio.currentTime + 0.12);
    } catch {
      // No audio device, or a browser that will not start a context without a gesture. Silence is an acceptable outcome; the visible warning is still there.
    }
  }, []);

  const focusFirst = (container: HTMLElement | null) =>
    container?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();

  useCounterHotkeys({
    enabled: !completed && !pendingProduct,
    onCustomerSearch: () => customerField.current?.focus(),
    onProductSearch: () => focusFirst(scanArea.current),
    onPayment: () => focusFirst(tenderArea.current),
    onComplete: () => {
      if (lines.length > 0 && activeBranch && !createSale.isPending) handleCheckout();
    },
    onEscape: () => {
      if (pendingProduct) return setPendingProduct(null);
      setSelectedLineId(null);
      focusFirst(scanArea.current);
    },
    onMoveSelection: (delta) => {
      if (lines.length === 0) return;
      const index = lines.findIndex((line) => line.lineId === selectedLineId);
      const next = index === -1 ? (delta === 1 ? 0 : lines.length - 1) : index + delta;
      const clamped = Math.max(0, Math.min(lines.length - 1, next));
      setSelectedLineId(lines[clamped]?.lineId ?? null);
    },
    onAdjustQuantity: (delta) => {
      if (!selectedLineId) return;
      const line = lines.find((entry) => entry.lineId === selectedLineId);
      if (!line) return;
      updateLine(selectedLineId, { quantity: Math.max(1, line.quantity + delta) });
    },
    onRemoveLine: () => {
      if (!selectedLineId) return;
      removeLine(selectedLineId);
      setSelectedLineId(null);
    },
  });

  if (completed) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('sales.counter.title')} subtitle={t('sales.counter.subtitle')} />

        <Alert tone="success">
          {t('sales.counter.completed', {
            reference: completed.reference,
            total: format(completed.totalMinor),
          })}
        </Alert>

        <Card>
          <CardHeader
            title={completed.reference}
            subtitle={t('sales.counter.completedHint')}
            action={
              <div className="flex flex-wrap gap-2">
                {completed.invoices[0] ? (
                  <Button
                    variant="secondary"
                    onClick={() => navigate(`/sales/invoices/${completed.invoices[0]?.id ?? ''}`)}
                  >
                    <Receipt className="size-4" aria-hidden />
                    {t('sales.counter.viewInvoice')}
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  onClick={() =>
                    window.open(
                      `/print/receipt/${completed.id}`,
                      '_blank',
                      'noopener,width=420,height=640',
                    )
                  }
                >
                  <Receipt className="size-4" aria-hidden />
                  {t('sales.print.printReceipt')}
                </Button>

                <Button onClick={() => setCompleted(null)}>
                  <Plus className="size-4" aria-hidden />
                  {t('sales.counter.newSale')}
                </Button>
              </div>
            }
          />

          <dl className="grid gap-3 p-4 sm:grid-cols-3">
            <Figure label={t('sales.fields.total')} value={format(completed.totalMinor)} strong />
            <Figure label={t('sales.fields.paid')} value={format(completed.paidMinor)} />
            <Figure label={t('sales.fields.balance')} value={format(completed.balanceMinor)} />
          </dl>
        </Card>
      </div>
    );
  }

  const conflictName = customerConflict
    ? (customerConflict.customerLabel ?? customerConflict.customerId)
    : '';

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('sales.counter.title')}
        subtitle={t('sales.counter.subtitle')}
        actions={
          lines.length > 0 || canStartOpticalOrder ? (
            <div className="flex flex-wrap gap-2">
              {canStartOpticalOrder ? (
                <Button variant="secondary" onClick={startOpticalOrder}>
                  <Glasses className="size-4" aria-hidden />
                  {t('sales.counter.opticalOrder.create')}
                </Button>
              ) : null}
              {lines.length > 0 ? (
                <Button variant="secondary" onClick={clearBasket}>
                  <X className="size-4" aria-hidden />
                  {t('sales.counter.clear')}
                </Button>
              ) : null}
            </div>
          ) : undefined
        }
      />

      {!activeBranch ? <Alert tone="warning">{t('sales.counter.selectBranch')}</Alert> : null}
      {error ? <Alert tone="error">{error}</Alert> : null}

      <ShortcutLegend />

      {!isOnline ? (
        <Alert tone="warning">
          {t('sales.counter.offline')}
          <span className="mt-1 block text-xs">{t('sales.counter.offlineHint')}</span>
        </Alert>
      ) : null}

      {needsAcknowledgement && validation ? (
        <Alert tone={validation.isUnchanged ? 'info' : 'warning'}>
          <span className="block">
            {validation.isUnchanged
              ? t('sales.counter.draftRestored')
              : t('sales.counter.draftChanged')}
          </span>
          {validation.lines
            .filter((line) => line.issues.length > 0)
            .map((line, index) => (
              <span key={line.lineKey ?? index} className="mt-1 block text-xs">
                {line.sku || line.variantId} —{' '}
                {line.issues.map((issue) => t(`sales.counter.issues.${issue}`)).join(' · ')}
              </span>
            ))}
          {validation.customerIssues.map((issue) => (
            <span key={issue} className="mt-1 block text-xs">
              {t(`sales.counter.customerIssues.${issue}`)}
            </span>
          ))}
          <Button
            size="sm"
            variant="secondary"
            className="mt-2"
            onClick={() => setNeedsAcknowledgement(false)}
          >
            {t('sales.counter.acknowledge')}
          </Button>
        </Alert>
      ) : null}

      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Card>
            <CardHeader
              title={t('sales.counter.addProduct')}
              subtitle={t('sales.counter.addHint')}
            />
            <div ref={scanArea} className="space-y-3 p-4 pt-0">
              {lensRefused ? (
                <Alert tone="warning">
                  <span className="block">{t('sales.counter.opticalOrder.lensRefused')}</span>
                  {canStartOpticalOrder ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="mt-2"
                      onClick={startOpticalOrder}
                    >
                      <Glasses className="size-4" aria-hidden />
                      {t('sales.counter.opticalOrder.create')}
                    </Button>
                  ) : null}
                </Alert>
              ) : null}
              <ProductSearch
                onSelectProduct={handleProduct}
                onSelectVariant={addVariant}
                autoFocus
                onScanFailure={soundScanFailure}
              />
            </div>
          </Card>

          {pendingProduct ? (
            <VariantChooser
              product={pendingProduct}
              onPick={addVariant}
              onCancel={() => setPendingProduct(null)}
            />
          ) : null}

          <Card>
            <CardHeader
              title={t('sales.counter.basket')}
              subtitle={t('sales.counter.lineCount', { count: lines.length })}
            />

            {lines.length === 0 ? (
              <EmptyState
                icon={ShoppingCart}
                title={t('sales.counter.emptyBasket')}
                description={t('sales.counter.emptyBasketHint')}
              />
            ) : (
              <ul className="divide-line divide-y">
                {lines.map((line) => {
                  const recommendation = line.recommendation;
                  const reasons = (recommendation?.reasonCodes ?? [])
                    .filter((code) => KNOWN_REASON_CODES.has(code))
                    .map((code) => t(`frameRecommendations.reasons.${code}`));
                  const isRefused =
                    recommendation !== null &&
                    (refusedLineIds.has(line.lineId) || !canSellRecommendations);

                  return (
                    <li
                      key={line.lineId}
                      aria-current={line.lineId === selectedLineId ? 'true' : undefined}
                      onClick={() => setSelectedLineId(line.lineId)}
                      className={cn(
                        'flex flex-wrap items-center gap-3 p-4',
                        line.lineId === selectedLineId &&
                          'bg-brand-50 ring-brand-300 ring-1 ring-inset',
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-ink block truncate font-medium">{line.name}</span>
                          {recommendation ? (
                            <Badge tone="brand">{t('sales.counter.recommendation.badge')}</Badge>
                          ) : null}
                        </span>
                        <span className="text-muted font-mono text-xs" dir="ltr">
                          <bdi>{line.sku}</bdi> · {format(line.unitPriceMinor)}
                        </span>
                        {reasons.length > 0 ? (
                          <details className="text-muted mt-1 text-xs">
                            <summary className="cursor-pointer">
                              {t('sales.counter.recommendation.why')}
                            </summary>
                            <ul className="mt-1 list-inside list-disc space-y-0.5">
                              {reasons.map((reason) => (
                                <li key={reason}>{reason}</li>
                              ))}
                            </ul>
                          </details>
                        ) : null}
                        {isRefused ? (
                          <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-amber-800">
                            <span>{t('sales.counter.recommendation.refused')}</span>
                            <Button
                              size="sm"
                              variant="secondary"
                              aria-label={t('sales.counter.recommendation.removeFor', {
                                sku: line.sku,
                              })}
                              onClick={(event) => {
                                event.stopPropagation();
                                removeRecommendation(line.lineId);
                              }}
                            >
                              {t('sales.counter.recommendation.remove')}
                            </Button>
                          </span>
                        ) : null}
                        <label className="text-muted mt-1 flex items-center gap-1.5 text-xs">
                          <input
                            type="checkbox"
                            checked={line.collectLater}
                            onChange={(event) =>
                              updateLine(line.lineId, { collectLater: event.target.checked })
                            }
                          />
                          {t('sales.optical.collectLater')}
                        </label>
                      </span>

                      <span className="w-20">
                        <Input
                          type="number"
                          min={1}
                          value={String(line.quantity)}
                          aria-label={t('sales.counter.quantityFor', { sku: line.sku })}
                          onChange={(event) =>
                            updateLine(line.lineId, {
                              quantity: Math.max(1, Number(event.target.value) || 1),
                            })
                          }
                        />
                      </span>

                      <span className="text-ink w-24 text-end font-medium" dir="ltr">
                        {format(line.unitPriceMinor * line.quantity - line.discountMinor)}
                      </span>

                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t('sales.counter.removeLine', { sku: line.sku })}
                        onClick={() => removeLine(line.lineId)}
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader title={t('sales.counter.summary')} />
            <div ref={tenderArea} className="space-y-3 p-4 pt-0">
              {(taxProfiles.data?.length ?? 0) > 1 ? (
                <FormField label={t('sales.fields.taxProfile')}>
                  {({ id }) => (
                    <Select
                      id={id}
                      options={(taxProfiles.data ?? []).map((profile) => ({
                        value: profile.id,
                        label: `${profile.nameEn} · ${(profile.rateBasisPoints / 100).toFixed(2)}%`,
                      }))}
                      value={activeProfile?.id ?? ''}
                      onChange={(event) => setTaxProfileId(event.target.value)}
                    />
                  )}
                </FormField>
              ) : null}

              <Input
                ref={customerField}
                label={t('sales.fields.customerOptional')}
                placeholder={t('sales.counter.customerPlaceholder')}
                value={customerId}
                disabled={addingRecommendation}
                onChange={(event) => changeCustomer(event.target.value.trim())}
              />

              <dl className="space-y-2 pt-1">
                <Figure label={t('sales.fields.subtotal')} value={format(totals.subtotal)} />
                {totals.discount > 0 ? (
                  <Figure
                    label={t('sales.fields.discount')}
                    value={`− ${format(totals.discount)}`}
                  />
                ) : null}
                <Figure
                  label={t('sales.fields.tax', {
                    rate: ((activeProfile?.rateBasisPoints ?? 0) / 100).toFixed(2),
                  })}
                  value={format(totals.tax)}
                />
                <div className="border-line border-t pt-2">
                  <Figure label={t('sales.fields.total')} value={format(totals.total)} strong />
                </div>
              </dl>

              <p className="text-muted text-xs">
                {t('sales.counter.currencyNote', { currency: currencyCode })}
              </p>
            </div>
          </Card>

          <TenderPanel
            tenders={tenders}
            totalMinor={totals.total}
            outstandingMinor={outstanding}
            onChange={setTenders}
            onPendingChange={setTenderPending}
          />

          {tenderPending ? <Alert tone="warning">{t('sales.tender.notAdded')}</Alert> : null}

          <Button
            className="w-full"
            isLoading={createSale.isPending}
            disabled={
              lines.length === 0 ||
              !activeBranch ||
              !isOnline ||
              needsAcknowledgement ||
              addingRecommendation ||
              tenderPending
            }
            onClick={handleCheckout}
          >
            <Receipt className="size-4" aria-hidden />
            {t('sales.counter.checkout', { total: format(totals.total) })}
          </Button>
        </div>
      </div>

      {/* Each dialog is mounted only while it is asked, so the page never holds two dialogs whose titles share an id. */}
      {customerConflict ? (
        <ConfirmDialog
          isOpen
          title={t('sales.counter.handoff.conflictTitle')}
          message={t('sales.counter.handoff.conflictBody', { name: conflictName })}
          cancelLabel={t('sales.counter.handoff.keep')}
          confirmLabel={t('sales.counter.handoff.clearAndStart', { name: conflictName })}
          isDestructive
          onCancel={() => setCustomerConflict(null)}
          onConfirm={() => {
            const chosen = customerConflict;
            setCustomerConflict(null);
            const recommendation = recommendationFromToken(chosen.selectionToken);
            if (!recommendation) return;
            // Clears the current basket only; a basket set aside after a failed check is in another slot and stays there.
            clearBasket();
            void addRecommendedFrame(chosen, recommendation);
          }}
        />
      ) : null}

      {confirmingOpticalOrder ? (
        <ConfirmDialog
          isOpen
          title={t('sales.counter.opticalOrder.separateTitle')}
          message={t('sales.counter.opticalOrder.separateBody')}
          cancelLabel={t('sales.counter.opticalOrder.stay')}
          confirmLabel={t('sales.counter.opticalOrder.start')}
          onCancel={() => setConfirmingOpticalOrder(false)}
          onConfirm={openOpticalOrder}
        />
      ) : null}

      {pendingCustomer !== null ? (
        <ConfirmDialog
          isOpen
          title={t('sales.counter.customerChange.title')}
          message={t('sales.counter.customerChange.body', { count: recommendedLineCount })}
          cancelLabel={t('sales.counter.customerChange.keep')}
          confirmLabel={t('sales.counter.customerChange.confirm')}
          onCancel={() => setPendingCustomer(null)}
          onConfirm={confirmCustomerChange}
        />
      ) : null}
    </div>
  );
}

function Figure({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className={strong ? 'text-ink font-medium' : 'text-muted text-sm'}>{label}</dt>
      <dd className={strong ? 'text-ink text-lg font-semibold' : 'text-ink text-sm'} dir="ltr">
        {value}
      </dd>
    </div>
  );
}

/**
 * Which variant of a browsed product.
 *
 * Skipped entirely for a scan, where the barcode already names the exact one.
 */
function VariantChooser({
  product,
  onPick,
  onCancel,
}: {
  product: ProductSummary;
  onPick: (variant: ProductVariantSummary, product: ProductSummary) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const { data, isPending } = useProductQuery(product.id);

  const variants = (data?.variants ?? []).filter((variant) => variant.isActive);

  useEffect(() => {
    // Nothing to choose between when there is only one.
    if (variants.length === 1 && variants[0]) onPick(variants[0], product);
  }, [variants, product, onPick]);

  if (isPending) return null;
  if (variants.length <= 1) return null;

  return (
    <Card>
      <CardHeader
        title={t('sales.counter.chooseVariant')}
        subtitle={product.name}
        action={
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        }
      />
      <ul className="divide-line divide-y">
        {variants.map((variant) => (
          <li key={variant.id}>
            <button
              type="button"
              className="hover:bg-surface-2 flex w-full items-center justify-between gap-3 p-4 text-start"
              onClick={() => onPick(variant, product)}
            >
              <ProductThumb image={variant.image} alt={variant.variantName ?? product.name} />
              <span className="min-w-0 flex-1">
                <span className="text-ink block truncate font-medium">
                  {variant.variantName ?? product.name}
                </span>
                <span className="text-muted font-mono text-xs" dir="ltr">
                  {variant.sku}
                </span>
              </span>
              <span className="text-ink font-medium" dir="ltr">
                {format(variant.sellingPriceMinor)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
